import React, { useContext, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import { getCheapestPackagesByRoom } from 'selectors/offerPage/bedbankMultipleRoomBookingSelectors'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { createOfferStructuredData } from 'components/utils/OfferStructuredData'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'

interface Props {
  offer: App.BedbankOffer
}

function HotelStructuredData(props: Props) {
  const { offer } = props
  const utmBp = useAppSelector(state => state.utm.bp)
  const geo = useContext(GeoContext)
  const contextCurrency = useContext(CurrencyContext)
  const { checkIn, checkOut } = useAppSelector((state) => state.router.location.query)
  const offerPageState = useContext(OfferPageStateContext)

  const [packageViews, fetchingRates] = useBedbankPackageViews(offer)
  const cheapestPackageSelectionsByRoom = useAppSelector(appState => getCheapestPackagesByRoom(offerPageState, appState))

  const availablePackageRates = useMemo(() =>
    packageViews.flatMap(packageView => {
      if (fetchingRates || !packageView.show || !packageView.available) {
        return
      }
      return packageView.rates.map(rate => {
        if (rate.isFlightBundle) {
          return
        }
        return rate.totals
      })
    }).filter(Boolean),
  [fetchingRates, packageViews])

  const currency = contextCurrency ?? geo.currentCurrency

  const pricingData = useMemo(() => {
    if (utmBp !== '3' || !checkIn || !checkOut) {
      return {}
    }

    // if utm_bp=3, Get the hotelOnly key and extract the rate object
    const hotelOnly = cheapestPackageSelectionsByRoom?.hotelOnly
    if (!hotelOnly || typeof hotelOnly !== 'object') {
      return {}
    }

    const hotelOnlyKeys = Object.keys(hotelOnly)
    const firstKey = hotelOnlyKeys[0]
    if (!firstKey) {
      return {}
    }

    const firstSelection = hotelOnly[firstKey]
    const totals = firstSelection?.rate?.totals

    if (!totals?.total || totals.total === 0) {
      return {}
    }

    const { propertyFees, total = 0, exclusive, taxesAndFees } = totals

    const allOffers = availablePackageRates?.length > 0 ? availablePackageRates.map(total => createOfferStructuredData(checkIn, checkOut, {
      propertyFees: total?.propertyFees,
      offerPrice: total?.total || 0,
      offerPriceBeforeTax: total?.exclusive,
      taxesAndFees: total?.taxesAndFees,
    }, currency)) : []

    return {
      makesOffer: [createOfferStructuredData(checkIn, checkOut, {
        propertyFees,
        offerPrice: total,
        offerPriceBeforeTax: exclusive,
        taxesAndFees,
      }, currency),
      ...allOffers,
      ],
    }
  }, [utmBp, checkIn, checkOut, cheapestPackageSelectionsByRoom, currency, availablePackageRates])

  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Hotel',
    name: offer.name,
    description: offer.metaDescription,
    identifier: offer.id,
    address: {
      '@type': 'PostalAddress',
      addressCountry: offer.property.address.countryCode,
      addressRegion: offer.property.address.stateProvinceName,
      addressLocality: offer.property.address.city,
      postalCode: offer.property.address.postalCode,
      streetAddress: offer.property.address.line1 + (offer.property.address.line2 ? ' ' + offer.property.address.line2 : ''),
    },
    ...pricingData,
    telephone: offer.property.phone,
  }), [offer, pricingData])

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  )
}

export default React.memo(HotelStructuredData)
